/**
 * chartMind7PromptGroups.js — Query Manager grouping for ChartMind-7 combinations.
 * Shaped by: CM7-07; decision D-CM7-12.
 * Invariants: UI grouping only — storage is per promptId.
 *   Combination ids are not top-level cards. Default/Default stays the base id.
 * Related: chartMind7PromptRegistry.js, PromptsManagerPage.jsx.
 */

import {
  CM7_MODES,
  CM7_SETTINGS,
  buildComboPromptId,
  isAllowedComboPromptId,
  listEnabledSpecialties,
  parseComboPromptId,
} from "features/chartMind7/data/chartMind7PromptRegistry";

const BASE_IDS = new Set(Object.values(CM7_MODES));

export function isChartMind7ComboPromptId(promptId) {
  return isAllowedComboPromptId(promptId);
}

function isPending(row) {
  return row?.reviewStatus === "physician_review_pending";
}

function shellFor(base, specialtyKey, setting) {
  return {
    ...base,
    id: buildComboPromptId(base.id, specialtyKey, setting),
    specialtyKey,
    setting,
    reviewStatus: null,
    organizationPrompt: "",
    regionalPrompt: "",
    systemPrompt: "",
    combinations: undefined,
    pendingCount: 0,
  };
}

/**
 * Hide combination rows from the card list and hang them on the base card.
 * The six ChartMind-7 bases also get a shell for each pair when the P1 row
 * is not in this list (org admins cannot read llmGlobalPrompts).
 */
export function groupChartMind7PromptRows(rows) {
  const combinationsByBase = new Map();
  const bases = [];

  (rows || []).forEach((row) => {
    const parsed = parseComboPromptId(row?.id);
    if (parsed && isAllowedComboPromptId(row.id)) {
      const list = combinationsByBase.get(parsed.baseId) || [];
      list.push({
        ...row,
        specialtyKey: parsed.specialtyKey,
        setting: parsed.setting,
      });
      combinationsByBase.set(parsed.baseId, list);
      return;
    }
    bases.push(row);
  });

  return bases.map((base) => {
    const loaded = combinationsByBase.get(base.id) || [];
    const byKey = new Map(loaded.map((row) => [`${row.specialtyKey}--${row.setting}`, row]));
    const combinations = BASE_IDS.has(base.id)
      ? listEnabledSpecialties().flatMap((specialty) =>
          CM7_SETTINGS.map((setting) => {
            const key = `${specialty.key}--${setting}`;
            return byKey.get(key) || shellFor(base, specialty.key, setting);
          }),
        )
      : loaded;
    const pendingCount = combinations.filter(isPending).length;
    return { ...base, combinations, pendingCount };
  });
}

export function chartMind7SpecialtyOptionLabel(specialty, combinations) {
  const pending = (combinations || []).some(
    (row) => row.specialtyKey === specialty.key && isPending(row),
  );
  return pending ? `${specialty.displayName} • pending` : specialty.displayName;
}

export function chartMind7SettingOptionLabel(setting, specialtyKey, combinations) {
  const name = setting === "inpatient" ? "Inpatient" : "Outpatient";
  const pending = (combinations || []).some(
    (row) =>
      row.specialtyKey === specialtyKey && row.setting === setting && isPending(row),
  );
  return pending ? `${name} • pending` : name;
}

export function resolveChartMind7PromptChoice(card, specialtyKey, setting) {
  const specialty = String(specialtyKey || "").trim();
  const place = String(setting || "").trim();
  if (!specialty && !place) {
    return { status: "base", prompt: card };
  }
  if (!specialty || !place) {
    return { status: "incomplete", prompt: null };
  }
  const match = (card?.combinations || []).find(
    (row) => row.specialtyKey === specialty && row.setting === place,
  );
  if (!match) return { status: "incomplete", prompt: null };
  return { status: "combination", prompt: match };
}
